import { Typography, Box, Popover, ClickAwayListener } from "@mui/material";
import React, { useCallback, useState } from "react";

interface MiniModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  width?: number | string;
  maxHeight?: string;
  anchorEl: HTMLElement | null;
}

export const MiniModal: React.FC<MiniModalProps> = ({
  open,
  onClose,
  title,
  children,
  width,
  maxHeight = "80vh",
  anchorEl,
}) => {
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      anchorPosition={{
        top: -8,
        left: -16,
      }}
      PaperProps={{
        sx: {
          minWidth: 200,
          maxWidth: { xs: "90vw", sm: "50vw", md: "35vw" },
          width: width || "auto",
          maxHeight,
          boxShadow: 3,
          borderRadius: 1,
          overflow: "visible", // Allow content to determine size
          // Ensure proper width calculation for wrapped text
          display: "inline-block",
        },
      }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box
          sx={{
            px: 2,
            py: 1,
            position: "relative",
            minHeight: 40,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {title && (
            <Typography
              variant="body2"
              fontSize="0.75rem"
              color="text.secondary"
              sx={{
                mb: 0.5,
                pr: 4,
                flexShrink: 0, // Don't shrink the title
                wordBreak: "break-word",
                overflowWrap: "break-word",
                hyphens: "auto",
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
          )}
          <Box
            sx={{
              flex: 1,
              minHeight: 0, // Allow flex item to shrink below content size
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word", // Additional break-word support
              hyphens: "auto", // Allow hyphenation for better wrapping
              overflow: "auto", // Handle overflow within content area
              maxHeight: "60vh", // Limit content area height
              lineHeight: 1.4, // Improve readability
            }}
          >
            {children}
          </Box>
        </Box>
      </ClickAwayListener>
    </Popover>
  );
};

export const useMiniModal = (enabled = true) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openModal = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!enabled) {
        return;
      }
      setAnchorEl(event.currentTarget);
      setModalOpen(true);
    },
    [enabled]
  );

  const closeModal = useCallback(() => {
    if (!enabled) {
      return;
    }
    setModalOpen(false);
    setAnchorEl(null);
  }, [enabled]);

  return {
    modalOpen,
    anchorEl,
    openModal,
    closeModal,
  };
};
